import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  matchGeoFirstAndLastCoords,
  useAddFieldSimulation,
  useDeleteField,
  useGetFarms,
  useGetFieldShape,
  useGetFieldSimulations,
  useNavigate,
  useTranslation,
} from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  FieldDetails as FieldDetailsType,
  FieldPracticeResponse,
  Geometry,
  PRACTICE_BUNDLE_SLUG,
  PracticeBundle,
  VIEW_TYPE,
} from "models";

import {
  Button,
  Drawer,
  FCPOMenu,
  Flex,
  HideFCPOFeatureForUnenrolledFarms,
  Icon,
  MapStatic,
  Modal,
  RecommendationSwitch,
  Spin,
  Message as message,
} from "components";

import { PracticeBundleControls } from "pages/app/all-field-view/components/PracticeBundleControls";
import { FieldDetails } from "pages/app/fields/components/FieldDetails";
import { SplitFieldDrawer } from "pages/app/fields/components/SplitFieldDrawer";

export type FieldTimelineHeaderProps = {
  availablePracticeBundles: FieldPracticeResponse["availablePracticeBundles"];
  fieldDetails: FieldDetailsType;
  showRecommendations: boolean;
  setShowRecommendations: Dispatch<SetStateAction<boolean>>;
  isFetchingCropRecommendations: boolean;
  setShowSimulations: Dispatch<SetStateAction<boolean>>;
  isEnrolled: boolean;
  isPracticeEnrollmentDisabled: boolean;
  setPracticeBundles?: Dispatch<SetStateAction<PracticeBundle[] | undefined>>;
  dataTestId?: string;
  className?: string;
  isPendingFieldDetails?: boolean;
};

export const FieldTimelineHeader = ({
  availablePracticeBundles,
  fieldDetails,
  showRecommendations,
  setShowRecommendations,
  isFetchingCropRecommendations,
  setShowSimulations,
  isEnrolled,
  isPracticeEnrollmentDisabled,
  setPracticeBundles,
  dataTestId,
  className,
  isPendingFieldDetails,
}: FieldTimelineHeaderProps) => {
  const { t } = useTranslation(["fieldTimeline", "farm"]);
  const navigate = useNavigate();
  const { currentFarm } = useGetFarms();

  const { fieldShape, fieldShapeLoading } = useGetFieldShape({ fieldId: fieldDetails?.id });
  const { mutateAsync: deleteFieldAsync } = useDeleteField();

  const [geometry, setGeometry] = useState<Geometry>();
  const [showFieldEditDrawer, setShowFieldEditDrawer] = useState(false);
  const [showDeleteFieldModal, setShowDeleteFieldModal] = useState(false);
  const [showFieldSplittingDrawer, setShowFieldSplittingDrawer] = useState(false);

  // TODO pass actual simulation name
  const { data: fieldSimulations } = useGetFieldSimulations({ fieldId: fieldDetails.id });
  const { mutate: addFieldSimulation } = useAddFieldSimulation({ fieldId: fieldDetails.id, name: "First Simulation" });

  const onModalSubmit = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const fieldId = fieldDetails?.id;
      if (!fieldId) {
        setShowDeleteFieldModal(false);
        return;
      }
      try {
        await deleteFieldAsync({ fieldId });
        navigate("/all-fields");
      } catch {
        message({ content: t("fieldTimeline:header.error.delete", { deletedField: fieldDetails?.name }), icon: "error" });
      } finally {
        setShowDeleteFieldModal(false);
      }
    },
    [deleteFieldAsync, navigate, fieldDetails?.id, fieldDetails?.name, setShowDeleteFieldModal, t]
  );

  const handleSimulationsClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!fieldSimulations?.length) addFieldSimulation();
    setShowSimulations(true);

    // remove focus from the button after the function runs to remove the css :focus styling
    if (e.currentTarget instanceof HTMLButtonElement) {
      e.currentTarget.blur();
    }
  };

  useEffect(() => {
    if (fieldShape && fieldShape.geometry) {
      setGeometry((prevGeometry) => {
        const newGeometry = matchGeoFirstAndLastCoords(fieldShape.geometry);

        // Compare previous geometry with the new one to prevent unnecessary state updates
        if (JSON.stringify(prevGeometry?.geometry) === JSON.stringify(newGeometry)) {
          return prevGeometry;
        }

        return { ...fieldShape, geometry: newGeometry };
      });
    }
  }, [fieldShape, fieldShapeLoading]);

  const isLoadingMap = isPendingFieldDetails || fieldDetails?.location?.lat === undefined || fieldDetails?.location?.lng === undefined;
  const isEnabledProgramFarm = !!currentFarm?.carbonProgram?.isEnabled;
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  return (
    <div className={className} data-testid={dataTestId}>
      <Flex align="center" justify="space-between">
        <Flex align="center" justify="space-between">
          <Flex justify="center" align="center" className={`map-container ${isLoadingMap ? "map-loading" : ""}`} data-testid="map-container">
            {/* @ts-expect-error need to convert to tsx */}
            {isLoadingMap ? <Spin /> : <MapStatic field={fieldDetails} geometry={geometry} location={fieldDetails?.location} marker />}
          </Flex>
          {availablePracticeBundles?.length && (
            <HideFCPOFeatureForUnenrolledFarms>
              <Flex className="practice-bundles-container" align="center">
                <PracticeBundleControls
                  bundles={availablePracticeBundles.filter((practiceBundle) => practiceBundle.slug !== PRACTICE_BUNDLE_SLUG.CROPS)}
                  optionalInputsAreShown={false}
                  setOptionalInputsAreShown={() => {}}
                  isCurrentYear={true}
                  viewType={VIEW_TYPE.SINGLE_FIELD}
                  isHeader={true}
                />
              </Flex>
            </HideFCPOFeatureForUnenrolledFarms>
          )}
        </Flex>
        <Flex className="recommendation-simulation-toggles-container" align="center" gap="1rem">
          <RecommendationSwitch
            showRecommendations={showRecommendations}
            setShowRecommendations={setShowRecommendations}
            isFetchingCropRecommendations={isFetchingCropRecommendations}
          />
          {!isInventoryProgram && isEnabledProgramFarm && (
            <Button type="primary" className="simulation-toggle" onClick={handleSimulationsClick}>
              {t("header.simulationButtonText")}
            </Button>
          )}
          <FCPOMenu
            availablePracticeBundles={availablePracticeBundles}
            carbonProgram={currentFarm?.carbonProgram}
            fieldId={fieldDetails?.id}
            isEnrolled={isEnrolled}
            setShowFieldEditDrawer={setShowFieldEditDrawer}
            setShowDeleteFieldModal={setShowDeleteFieldModal}
            isPracticeEnrollmentDisabled={isPracticeEnrollmentDisabled}
            fieldDetails={fieldDetails}
            setPracticeBundles={setPracticeBundles}
            placement="left"
            viewType={VIEW_TYPE.SINGLE_FIELD}
          />
        </Flex>
      </Flex>
      {showFieldEditDrawer && (
        <Drawer
          title={t("farm:editFieldDrawerTitle")}
          visible={showFieldEditDrawer}
          dataTestId="edit-field-details-drawer"
          onClose={() => setShowFieldEditDrawer(false)}
        >
          <FieldDetails fieldId={fieldDetails?.id} setShowFieldEditDrawer={setShowFieldEditDrawer} setShowFieldSplittingDrawer={setShowFieldSplittingDrawer} />
        </Drawer>
      )}
      <Modal
        showModal={showDeleteFieldModal}
        onOk={onModalSubmit}
        onCancel={() => {
          setShowDeleteFieldModal(false);
        }}
        okText={t("farm:deleteModalConfirm.deleteText")}
        cancelText={t("farm:deleteModalConfirm.cancel")}
        zIndex={9999}
        destroyOnHidden={true}
        title={
          <>
            <Icon iconName="tabler/alert-circle" className="inline icon margin-right-75" size="m" fill="#DB3804" color="#FFF" />
            <span className="headline-3">{t("farm:deleteModalConfirm.fieldTitle", { fieldName: fieldDetails?.name })}</span>
          </>
        }
        content={<span className="paragraph-m">{t("farm:deleteModalConfirm.fieldMessage", { fieldName: fieldDetails?.name })}</span>}
      />
      {showFieldSplittingDrawer && geometry && (
        <SplitFieldDrawer field={fieldDetails} geometry={geometry} onClose={() => setShowFieldSplittingDrawer(false)} visible={showFieldSplittingDrawer} />
      )}
    </div>
  );
};
